import { useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useQueryClient } from "@tanstack/react-query";

import { fetchTenants, lookupPws, onboardPws } from "@/api/endpoints";
import { InfoTip } from "@/components/InfoTip";
import { TenantCombobox } from "@/components/TenantCombobox";
import type { OnboardingResult, PwsLookup } from "@/types/api";
import { usePageTitle } from "@/hooks/usePageTitle";

type TenantMode = "new" | "existing";

export function PwsOnboarding(): JSX.Element {
  usePageTitle("Add a PWS");
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [pwsId, setPwsId] = useState("");
  const [preview, setPreview] = useState<PwsLookup | null>(null);
  const [looking, setLooking] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);

  const [mode, setMode] = useState<TenantMode>("new");
  const [newTenantName, setNewTenantName] = useState("");
  const [tenantId, setTenantId] = useState("");

  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [result, setResult] = useState<OnboardingResult | null>(null);

  // Tenants are only needed for the "existing" path; load lazily.
  const tenantsQuery = useQuery({
    queryKey: ["tenants"],
    queryFn: fetchTenants,
    enabled: mode === "existing",
    staleTime: 5 * 60 * 1000,
  });

  function resetPreview(): void {
    setPreview(null);
    setLookupError(null);
    setAddError(null);
    setResult(null);
  }

  async function onLookup(e: FormEvent): Promise<void> {
    e.preventDefault();
    resetPreview();
    const id = pwsId.trim();
    if (!id) {
      setLookupError("Enter a PWS ID.");
      return;
    }
    setLooking(true);
    try {
      const found = await lookupPws(id);
      setPreview(found);
      // Default the new-tenant name to the FDEP-canonical system name.
      setNewTenantName(found.pws_name);
    } catch (err) {
      setLookupError(
        err instanceof ApiError
          ? err.status === 404
            ? `No PWS with ID "${id}" found in FDEP. Check the number.`
            : err.message
          : "Lookup failed.",
      );
    } finally {
      setLooking(false);
    }
  }

  async function onAdd(): Promise<void> {
    if (!preview) return;
    setAddError(null);
    if (mode === "new" && !newTenantName.trim()) {
      setAddError("Enter a customer/tenant name.");
      return;
    }
    if (mode === "existing" && !tenantId) {
      setAddError("Pick a tenant.");
      return;
    }
    setAdding(true);
    try {
      const res = await onboardPws({
        pws_id: preview.pws_id,
        ...(mode === "new"
          ? { new_tenant_name: newTenantName.trim() }
          : { tenant_id: tenantId }),
      });
      setResult(res);
      // The write created a tenant and/or assignment — every cached list of
      // systems or tenants is now stale (previously nothing was invalidated
      // and the new tenant was missing from the combobox for 5 minutes).
      void queryClient.invalidateQueries({ queryKey: ["systems"] });
      void queryClient.invalidateQueries({ queryKey: ["tenants"] });
      void queryClient.invalidateQueries({ queryKey: ["tenants-overview"] });
    } catch (err) {
      setAddError(err instanceof ApiError ? err.message : "Could not add the system.");
    } finally {
      setAdding(false);
    }
  }

  const tenants = tenantsQuery.data ?? [];

  return (
    <div className="space-y-6 max-w-3xl">
      <header className="flex items-baseline justify-between">
        <h1 className="text-xl font-semibold">Add a PWS</h1>
        <p className="text-sm text-ink-300">
          Look up a public water system by its FDEP ID, then assign it to a customer.
        </p>
      </header>

      {/* Step 1 — look up the PWS in FDEP */}
      <form onSubmit={(e) => void onLookup(e)} className="card space-y-4">
        <h2 className="card-title">1. Find the system</h2>
        <div className="flex flex-wrap items-end gap-3">
          <label className="block space-y-1 flex-1 min-w-[12rem]">
            <span className="text-sm text-ink-200 inline-flex items-center">
              PWS ID
              <InfoTip
                label="PWS ID"
                body="The FDEP Public Water System identifier (a 7-digit number). We verify it against FDEP before adding so typos can't create a phantom system."
              />
            </span>
            <input
              className="input"
              inputMode="numeric"
              placeholder="e.g. 6534609"
              value={pwsId}
              onChange={(e) => {
                setPwsId(e.target.value);
                if (preview) resetPreview();
              }}
            />
          </label>
          <button type="submit" className="btn-primary" disabled={looking}>
            {looking ? "Looking up…" : "Look up"}
          </button>
        </div>
        {lookupError && <p className="text-sm text-severity-high">{lookupError}</p>}
      </form>

      {/* Step 2 — confirm + assign */}
      {preview && !result && (
        <section className="card space-y-4">
          <h2 className="card-title">2. Confirm &amp; assign</h2>

          <div className="rounded-md bg-ink-800/60 ring-1 ring-ink-700 p-4">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-lg font-semibold">{preview.pws_name}</span>
              <span className={preview.active ? "pill-ok" : "pill-medium"}>
                {preview.status ?? (preview.active ? "ACTIVE" : "UNKNOWN")}
              </span>
            </div>
            <dl className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-sm">
              <Fact label="PWS ID" value={preview.pws_id} />
              <Fact label="Type" value={preview.pws_type ?? "—"} />
              <Fact label="City" value={preview.city ?? "—"} />
              <Fact
                label="Population"
                value={preview.population != null ? preview.population.toLocaleString() : "—"}
              />
            </dl>
            {!preview.active && (
              <p className="mt-2 text-xs text-severity-medium">
                Heads up: FDEP marks this system as not active.
              </p>
            )}
          </div>

          {preview.already_assigned ? (
            <p className="text-sm text-severity-high">
              This PWS is already onboarded
              {preview.assigned_tenant_name ? (
                <>
                  {" "}
                  under <strong>{preview.assigned_tenant_name}</strong>
                </>
              ) : null}
              . Nothing to do.
            </p>
          ) : (
            <>
              <fieldset className="space-y-3">
                <legend className="text-sm text-ink-200 mb-1">Assign to</legend>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="tenant-mode"
                    checked={mode === "new"}
                    onChange={() => setMode("new")}
                  />
                  New customer (creates a tenant)
                </label>
                {mode === "new" && (
                  <label className="block space-y-1 ml-6">
                    <span className="text-sm text-ink-200">Customer / tenant name</span>
                    <input
                      className="input"
                      value={newTenantName}
                      onChange={(e) => setNewTenantName(e.target.value)}
                    />
                    <span className="text-xs text-ink-400">
                      Defaults to the FDEP system name. A URL slug is generated automatically.
                    </span>
                  </label>
                )}
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="tenant-mode"
                    checked={mode === "existing"}
                    onChange={() => setMode("existing")}
                  />
                  Existing customer (add to a tenant that already exists)
                </label>
                {mode === "existing" && (
                  <div className="ml-6">
                    <TenantCombobox
                      tenants={tenants}
                      value={tenantId}
                      onChange={setTenantId}
                      loading={tenantsQuery.isLoading}
                      placeholder="Search tenants by name or slug…"
                    />
                    {tenantsQuery.error && (
                      <span className="text-xs text-severity-high">
                        Could not load tenants:{" "}
                        {tenantsQuery.error instanceof ApiError
                          ? tenantsQuery.error.message
                          : "error"}
                      </span>
                    )}
                  </div>
                )}
              </fieldset>

              {addError && <p className="text-sm text-severity-high">{addError}</p>}

              <button
                type="button"
                className="btn-primary"
                disabled={adding}
                onClick={() => void onAdd()}
              >
                {adding ? "Adding…" : "Add system"}
              </button>
            </>
          )}
        </section>
      )}

      {/* Step 3 — done */}
      {result && (
        <section className="card border-l-4 border-severity-low space-y-2">
          <h2 className="card-title">System added</h2>
          <p className="text-sm text-ink-200">
            <strong>{result.pws_name}</strong> ({result.pws_id}) is now tracked under{" "}
            <strong>{result.tenant_name}</strong>
            {result.tenant_created ? " (new tenant)" : ""}.
          </p>
          <p className="text-xs text-ink-400">
            {result.warmed
              ? "FDEP data was pre-loaded — the dashboard is ready now."
              : "FDEP data will load on first view. Set system attributes on the dashboard to clear any “needs input” rules."}
          </p>
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              className="btn-primary"
              onClick={() => navigate(`/systems/${encodeURIComponent(result.pws_id)}`)}
            >
              Open dashboard
            </button>
            <button
              type="button"
              className="text-sm text-ink-300"
              onClick={() => {
                setPwsId("");
                setTenantId("");
                setMode("new");
                resetPreview();
              }}
            >
              Add another
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <dt className="text-ink-400 text-xs">{label}</dt>
      <dd className="text-ink-100">{value}</dd>
    </div>
  );
}
